import React, { useState, ChangeEvent, FormEvent } from 'react';
import axios from 'axios';
import styles from './SupportForm.module.scss';

const SUPPORT_URL = 'https://webto.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8';
const ORG_ID = '00D1U000000Gl7T';
const MESSAGE_TIME_TO_SHOW = 7000;

enum MessageType {
  Success = 'success',
  Error = 'error'
}

export const SupportForm = () => {
  const [displayForm, setDisplayForm] = useState<boolean>(false);
  const [messageType, setMessageType] = useState<MessageType>(null);
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [spotID, setSpotID] = useState<string>('');
  const [domain, setDomain] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  const toggleFormDisplay = () => setDisplayForm(!displayForm);

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => setName(event.target.value);
  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value);
  const handleSpotIDChange = (event: ChangeEvent<HTMLInputElement>) => setSpotID(event.target.value);
  const handleDomainChange = (event: ChangeEvent<HTMLInputElement>) => setDomain(event.target.value);
  const handleSubjectChange = (event: ChangeEvent<HTMLInputElement>) => setSubject(event.target.value);
  const handleDescriptionChange = (event: ChangeEvent<HTMLTextAreaElement>) => setDescription(event.target.value);

  // The web to case salesforce API accepts only HTML forms
  const formData =
    `orgid=${encodeURIComponent(ORG_ID)}&` +
    `retURL=${encodeURIComponent(window.location.href)}&` +
    `name=${encodeURIComponent(name)}&` +
    `email=${encodeURIComponent(email)}&` +
    `spotID=${encodeURIComponent(spotID)}&` +
    `domain=${encodeURIComponent(domain)}&` +
    `subject=${encodeURIComponent(subject)}&` +
    `description=${encodeURIComponent(
      `Reporter: ${name}\nEmail: ${email}\nSpot_ID: ${spotID}\nDomain: ${domain}\n\nDescription: \n${description}\n\nSubmitted from: ${window.location.href}`
    )}&` +
    `external=${encodeURIComponent(1)}`;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    toggleFormDisplay();
    axios
      .post(SUPPORT_URL, formData, {
        // The web to case salesforce API accepts only HTML forms
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      })
      .then(function () {
        handleSetMessage(MessageType.Success);
      })
      .catch(function (error) {
        // There is a CORS error, but the form is sent
        if (error.message === 'Network Error') {
          handleSetMessage(MessageType.Success);
        } else {
          handleSetMessage(MessageType.Error);
          console.error(error);
        }
      });
  };

  const handleSetMessage = (type: MessageType) => {
    setMessageType(type);
    clearMessage();
  };

  const clearMessage = () => {
    setTimeout(() => {
      setMessageType(null);
    }, MESSAGE_TIME_TO_SHOW);
  };

  const renderMessage = () => {
    if (messageType === MessageType.Success) {
      return (
        <div className="success-message">
          <span className="text">Form submitted successfully</span>
        </div>
      );
    } else if (messageType === MessageType.Error) {
      return (
        <div className="error-message">
          <span className="text">
            Form submission failed. Please contact{' '}
            <a href="mailto:publisherfirst@openweb.com">publisherfirst@openweb.com</a>
          </span>
        </div>
      );
    }
    return null;
  };

  const renderForm = () => (
    <div className={styles.supportForm}>
      <div className={styles.supportHeader}>
        <b>Contact Us</b>
        <span className={styles.supportModalClose} onClick={toggleFormDisplay}>
          <b>
            <span>{String.fromCharCode(10005)}</span>
          </b>
        </span>
      </div>
      <div className={styles.supportFormBody}>
        <form onSubmit={handleSubmit}>
          <label htmlFor="name">
            Contact Name<span>*</span>
          </label>
          <input
            id="name"
            maxLength={80}
            size={20}
            type="text"
            onChange={handleNameChange}
            required
          />
          <br />
          <label htmlFor="email">
            Email<span>*</span>
          </label>
          <input
            id="email"
            maxLength={80}
            size={20}
            type="email"
            onChange={handleEmailChange}
            required
          />
          <br />
          <label htmlFor="spotID">
            Spot ID<span>*</span>
          </label>
          <input
            id="spotID"
            maxLength={80}
            size={20}
            type="text"
            onChange={handleSpotIDChange}
            required
          />
          <br />
          <label htmlFor="domain">
            Domain<span>*</span>
          </label>
          <input
            id="domain"
            maxLength={80}
            size={20}
            type="text"
            onChange={handleDomainChange}
            required
          />
          <br />
          <label htmlFor="subject">
            Subject<span>*</span>
          </label>
          <input
            id="subject"
            maxLength={80}
            size={20}
            type="text"
            onChange={handleSubjectChange}
            required
          />
          <br />
          <label htmlFor="description">
            Description<span>*</span>
          </label>
          <textarea id="description" onChange={handleDescriptionChange} required />
          <br />
          <button type="submit">Submit</button>
        </form>
      </div>
    </div>
  );

  const renderButton = () => (
    <button className={styles.supportButton} onClick={toggleFormDisplay}>
      Support
    </button>
  );

  return (
    <>
      {renderMessage()}
      {displayForm ? renderForm() : renderButton()}
    </>
  );
};
